import '../scss/dynamic-posts.scss'

import LayoutManager from './layout-manager/LayoutManager'
import GridLayoutManager from './layout-manager/GridLayoutManager'
import ListLayoutManager from './layout-manager/ListLayoutManager'
import ItemRepository from './ItemRepository'
import Pagination from './pagination/Pagination'
import Filters from './filters/Filters'
import MasonryLayoutManager from './layout-manager/MasonryLayoutManager'
import FlexGridLayoutManager from './layout-manager/FlexGridLayoutManager'
import SliderLayoutManager from './layout-manager/SliderLayoutManager'
import LoadMore from './loadmore/LoadMore'
import ProgressSpinner from './progress-spinner/ProgressSpinner'

import { instance as hooksManager } from './HooksManager'

const $ = jQuery

class DynamicPostWidget {
    element: JQuery

    layoutManager: LayoutManager

    pagination: Pagination
    loadMore: LoadMore
    emptyView: JQuery
    progressSpinner?: ProgressSpinner

    items: [
        {
            id: number
            categories: [[string]]
        },
    ]
    templateId: number
    blogId: number

    filters?: Filters
    lastFilterChange = 0

    itemRepository: ItemRepository

    constructor(element: JQuery) {
        element.data('initialized', true)
        this.element = element
        this.itemRepository = ItemRepository.getInstance(
            element.data('ajax-url'),
        )
        this.items = element.data('items')
        this.templateId = element.data('template')
        this.blogId = element.data('blog-id')

        const itemContainer = element.find('.dynamic-post-grid')
        switch (element.data('layout-type')) {
            case 'grid':
                this.layoutManager = new GridLayoutManager(itemContainer)
                break
            case 'list':
                this.layoutManager = new ListLayoutManager(itemContainer)
                break
            case 'masonry':
                this.layoutManager = new MasonryLayoutManager(itemContainer)
                break
            case 'flex_grid':
                this.layoutManager = new FlexGridLayoutManager(itemContainer)
                break
            case 'slider':
                this.layoutManager = new SliderLayoutManager(itemContainer)
                break
        }

        const customStyles = this.layoutManager.getCustomStyles(
            `#${this.element.attr('id')}`,
        )
        if (customStyles) {
            const style = $(`<style>${customStyles}</style>`)
            this.element.prepend(style)
        }

        const filters = element.find('.dynamic-post-filters')
        if (filters.length) {
            this.filters = new Filters(filters)
            this.filters.onFiltersChange = () => {
                this.lastFilterChange = +new Date()
                if (this.pagination) this.pagination.currentPage = 0
                this.updateVisibleItems()
            }
        }

        const pagination = element.find('.dynamic-post-pagination')
        if (pagination.length) {
            this.pagination = new Pagination(pagination)
            this.pagination.onPageChanged = (from, to) => {
                this.updateVisibleItems()
            }
        }

        const loadMore = element.find('.dynamic-post-loadmore')
        if (loadMore.length) {
            this.loadMore = new LoadMore(loadMore)
            this.loadMore.onLoadMore = () => {
                this.updateVisibleItems()
            }
        }

        const progressSpinner = element.find('.dynamic-post-progress')
        if (progressSpinner.length) {
            this.progressSpinner = new ProgressSpinner(progressSpinner)
        }

        this.emptyView = element.find('.dynamic-post-empty-view')

        this.updateVisibleItems()
    }

    async updateVisibleItems() {
        this.element.addClass('updating')
        this.progressSpinner?.show()

        this.layoutManager.onPreUpdate()
        hooksManager.triggerHook(this.element, 'onPreUpdate')

        const filterTimestamp = this.lastFilterChange
        let position = 0
        let skip = 0
        let totalItems = 0
        const missingViews = []
        const page = this.pagination?.currentPage || 0
        const maxVisibleItems =
            this.pagination?.itemsPerPage ||
            this.loadMore?.currentCount ||
            this.items.length

        for (const item of this.items) {
            let itemView = this.element.find(
                `.dynamic-post-grid > .grid-item#grid-item-${item.id}`,
            )
            const match = this.filters ? await this.filters.matches(item) : true
            if (match) totalItems++
            if (match && position < maxVisibleItems) {
                if (skip < maxVisibleItems * page) {
                    skip++
                    this.layoutManager.removeItem(itemView)
                    hooksManager.triggerHook(
                        this.element,
                        'onItemRemoved',
                        itemView,
                    )
                    continue
                }
                if (itemView.length) {
                    this.layoutManager.layoutItem(itemView, position)
                } else {
                    itemView = this.itemRepository.getItem(
                        item.id,
                        this.templateId,
                        this.blogId,
                    )
                    if (itemView != null) {
                        this.layoutManager.addItem(itemView)
                        hooksManager.triggerHook(
                            this.element,
                            'onItemAdded',
                            itemView,
                        )
                        this.layoutManager.layoutItem(itemView, position)
                    } else {
                        missingViews.push({
                            id: item.id,
                            position: position,
                        })
                    }
                }
                position++
            } else {
                this.layoutManager.removeItem(itemView)
                hooksManager.triggerHook(
                    this.element,
                    'onItemRemoved',
                    itemView,
                )
            }
        }

        const totalPages = Math.ceil(totalItems / maxVisibleItems)

        if (totalItems === 0) {
            this.element.find('.dynamic-post-empty-view').addClass('visible')
        } else {
            this.element.find('.dynamic-post-empty-view').removeClass('visible')
        }
        this.pagination?.update(totalPages)
        this.loadMore?.update(totalItems)

        this.layoutManager.visibleItems = position

        if (position > 0) {
            this.emptyView.css('display', 'none')
        } else {
            this.emptyView.css('display', 'block')
        }

        this.layoutManager.onMidUpdate()

        //updateGridDimensions(this.widgetInstance)

        await this.itemRepository.fetch(this.templateId, this.blogId)

        // Check if active filters and current page are still the same
        const isFilterUnchanged = this.lastFilterChange === filterTimestamp

        if (page !== (this.pagination?.currentPage || 0) || !isFilterUnchanged)
            return

        for (const missing of missingViews) {
            const itemView = this.itemRepository.getItem(
                missing.id,
                this.templateId,
                this.blogId,
            )
            this.layoutManager.addItem(itemView)
            hooksManager.triggerHook(this.element, 'onItemAdded', itemView)
            this.layoutManager.layoutItem(itemView, missing.position)
        }

        this.progressSpinner?.hide()
        this.element.removeClass('updating')
        this.layoutManager.onPostUpdate()
        this.loadMore?.onPostUpdate()
        hooksManager.triggerHook(this.element, 'onPostUpdate')
    }

    static initialize(element: JQuery) {
        if (element.data('initialized')) return
        new DynamicPostWidget(element)
    }
}

export default DynamicPostWidget
